import { useQuery } from "react-query";
import { getSignedUrlByKeyAsync } from "api/document";

/**
 * Custom hook to generate a signed URL for a treatment plan document from S3
 *
 * @param {Object} treatmentPlan
 * @param {boolean} enabled
 * @returns {Object}
 */
const useGetTreatmentPlanUrl = (treatmentPlan, enabled = true) => {
  const {
    data: treatmentPlanUrl,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["treatmentPlanUrl", treatmentPlan?.id],
    async () => {
      if (!treatmentPlan) {
        throw new Error("Treatment plan is required");
      }

      if (treatmentPlan.key) {
        return (
          await getSignedUrlByKeyAsync({
            filename: treatmentPlan.key,
            keyIsFullPath: true,
          })
        ).data?.url;
      } else if (treatmentPlan.cognito_id && treatmentPlan.filename) {
        return (
          await getSignedUrlByKeyAsync({
            cognitoId: treatmentPlan.cognito_id,
            filename: treatmentPlan.filename,
          })
        ).data?.url;
      } else {
        throw new Error("Treatment plan must have either 'key' or both 'cognito_id' and 'filename'");
      }
    },
    {
      enabled: enabled && !!treatmentPlan && !treatmentPlan?.panda_doc_session_id,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes - signed URLs are valid for longer
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error) => {
        console.error('Error generating signed URL:', error);
      },
    }
  );

  return {
    treatmentPlanUrl,
    isLoading,
    error,
    refetch,
  };
};

export default useGetTreatmentPlanUrl;
